<template>
    <div
        id="playAlongsPlayer"
        class="container collapsed fluid bg-white shadow tw-transition"
        :class="[collapsed ? 'collapsed-down' : '', !noSidebar ? 'with-sidebar' : '']"
    >
        <div
            v-if="loop"
            id="anchorA"
            class="loop-anchor bg-drumeo text-white body dense font-bold tw-rounded-full noselect pointer"
            :style="'left:' + anchorOffsets.a + '%;'"
            @mousedown="emitAnchorMouseDown('a')"
            @touchstart="emitAnchorMouseDown('a')"
        >
            A
        </div>

        <div
            v-if="loop"
            id="anchorB"
            class="loop-anchor bg-drumeo text-white body dense font-bold tw-rounded-full noselect pointer"
            :style="'left:' + anchorOffsets.b + '%;'"
            @mousedown="emitAnchorMouseDown('b')"
            @touchstart="emitAnchorMouseDown('b')"
        >
            B
        </div>

        <div
            class="player-tab title text-drumeo bg-white shadow flex-center pointer"
            @click.stop="collapsed = !collapsed"
        >
            <i
                class="fas"
                :class="collapsed ? 'fa-plus' : 'fa-minus'"
            ></i>
        </div>

        <div
            ref="progressBar"
            class="progress-container flex flex-row bg-grey-5 pointer"
            @mousedown="mousedown = true"
            @touchstart="mousedown = true"
        >
            <div
                class="progress-amount bg-drumeo"
                :style="durationOffsetStyles"
            ></div>
        </div>

        <div class="flex flex-row align-h-center pv-1 noselect">
            <div
                v-show="loop"
                class="flex flex-column align-center xs-4"
            >
                <button
                    class="btn collapse-square short mb-1"
                    @click.stop="emitAnchorButtonClick('a')"
                >
                    <span class="bg-black text-white">
                        A
                    </span>
                </button>

                <p class="tiny text-grey-4 dense">
                    {{ getAnchorOffsetTime(anchorOffsets.a) }}
                </p>
            </div>

            <div class="flex flex-column align-center grow no-events">
                <h4 class="title text-center tw-text-[#00101D]">
                    {{ $_title }}
                </h4>
                <h6 class="body text-center text-grey-4 mb-2">
                    <span class="capitalize">{{ $_style }}</span> @ {{ $_bpm }} BPM
                </h6>
                <h6 class="body dense text-grey-4 text-center">
                    {{ parseTime(currentTime) }} / {{ parseTime(totalDuration) }}
                </h6>
            </div>

            <div
                v-show="loop"
                class="flex flex-column align-center xs-4"
            >
                <button
                    class="btn collapse-square short mb-1"
                    @click.stop="emitAnchorButtonClick('b')"
                >
                    <span class="bg-black text-white">
                        B
                    </span>
                </button>

                <p class="tiny text-grey-4 dense">
                    {{ getAnchorOffsetTime(anchorOffsets.b) }}
                </p>
            </div>
        </div>

        <div class="flex flex-row align-h-center pv-1">
            <button
                class="btn collapse-square mh-1 tw-rounded-full"
                :disabled="disablePreviousTrack"
                @click.stop="previousTrack"
                @keyup.prevent
                @keydown.prevent
            >
                <span class="flat tw-text-black">
                    <i class="fas fa-step-backward tw-text-[#00101D]"></i>
                </span>
            </button>

            <button
                class="btn collapse-square mh-1 skip-5 tw-rounded-full"
                @click.stop="skipFive(false)"
                @keyup.prevent
                @keydown.prevent
            >
                <span class="flat tw-text-black">
                    <i class="fas fa-undo tw-text-[#00101D]"></i>
                </span>
            </button>

            <button
                class="btn collapse-square mh-1 tw-rounded-full"
                @click.stop="playPause"
                @keydown.prevent
            >
                <span class="flat tw-text-black">
                    <i
                        class="fas tw-text-[#00101D]"
                        :class="!audioPlayer.paused ? 'fa-pause' : 'fa-play'"
                    ></i>
                </span>
            </button>

            <button
                class="btn collapse-square mh-1 skip-5 tw-rounded-full"
                @click.stop="skipFive(true)"
                @keydown.prevent
            >
                <span class="flat tw-text-black">
                    <i class="fas fa-redo tw-text-[#00101D]"></i>
                </span>
            </button>

            <button
                class="btn collapse-square mh-1 tw-rounded-full"
                :disabled="isShuffle && playedContent.length === totalResults"
                @click.stop="nextTrack"
                @keydown.prevent
            >
                <span class="flat tw-text-black">
                    <i class="fas fa-step-forward tw-text-[#00101D]"></i>
                </span>
            </button>
        </div>

        <div class="flex flex-row bg-grey-5 pv-1 align-h-center">
            <div class="flex flex-column ph-1 grow"></div>
            <div class="flex flex-column player-buttons">
                <div class="flex flex-row">

                    <button
                        class="btn collapse-square mh-1 tw-rounded-full"
                        :title="metronome ? 'Disable Click Track' : 'Enable Click Track'"
                        @click.self.stop.prevent="toggleMetronome"
                        @keydown.prevent
                    >
                        <span
                            class="bg-white tw-rounded-full"
                            :class="metronome ? 'text-grey-5' : 'inverted text-white'"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" aria-labelledby="metronome" version="1.1" width="36" height="35" viewBox="0 0 36 35" class="tw-w-[20px]" fill="none">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M19.0243 23.6295H28.1505L23.6755 16.1825L19.0243 23.6295ZM23.8224 12.046L20.6592 6.78189C19.2551 4.44535 15.7449 4.44535 14.3409 6.78189L2.49419 26.4965C1.09007 28.833 2.84519 31.7537 5.65333 31.7537H29.3467C32.1549 31.7537 33.91 28.833 32.5059 26.4965L25.0172 14.0343L30.2258 5.69489C30.5282 5.21084 30.3808 4.57337 29.8968 4.27105C29.4128 3.96873 28.7753 4.11605 28.473 4.60009L23.8224 12.046ZM22.4807 14.1942L16.5877 23.6295H6.84952L15.9205 8.53429C16.6225 7.36601 18.3776 7.36601 19.0796 8.53429L22.4807 14.1942ZM4.86349 26.9346L5.60763 25.6962H29.3924L30.1366 26.9346C30.8386 28.1028 29.961 29.5632 28.557 29.5632H6.44305C5.03904 29.5632 4.16148 28.1028 4.86349 26.9346Z" fill="currentColor"/>
                            </svg>
                        </span>
                    </button>

                    <button
                        class="btn collapse-square mh-1 tw-rounded-full"
                        :title="drums ? 'Disable Drum Track' : 'Enable Drum Track'"
                        @click.stop="toggleDrums"
                        @keydown.prevent
                    >
                        <span
                            class="bg-white tw-rounded-full"
                            :class="drums ? 'text-grey-5' : 'inverted text-white'"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" aria-labelledby="drum-set" version="1.1" width="36" height="35" viewBox="0 0 36 35" class="tw-w-[20px]" fill="none">
                                <path d="M37.1446 24.719C37.1481 22.4284 36.5527 20.1754 35.4154 18.1752C34.278 16.175 32.6367 14.4945 30.6485 13.2946C28.6603 12.0946 26.3916 11.4153 24.0594 11.3215C21.7272 11.2278 19.4095 11.7227 17.3279 12.759C15.2463 13.7953 13.4705 15.3383 12.1702 17.2405C10.8699 19.1427 10.0887 21.3404 9.90118 23.6235C9.71369 25.9066 10.1262 28.1988 11.0994 30.2807C12.0725 32.3627 13.5736 34.1649 15.4593 35.5149L12.3138 39.1158L13.4642 40.0832L16.7379 36.336C18.7945 37.4993 21.1266 38.1117 23.5004 38.1117C25.8742 38.1117 28.2063 37.4993 30.263 36.336L33.5366 40.0832L34.687 39.1158L31.5416 35.5149C33.2761 34.2757 34.6877 32.6519 35.6612 30.7758C36.6348 28.8997 37.1428 26.8243 37.1437 24.719H37.1446ZM11.3731 24.719C11.371 21.9623 12.3432 19.2902 14.124 17.1581C15.9047 15.0261 18.3838 13.566 21.1388 13.0267C23.8937 12.4874 26.7539 12.9023 29.2321 14.2007C31.7102 15.4991 33.6527 17.6006 34.7287 20.147C35.8047 22.6935 35.9476 25.5273 35.1328 28.1655C34.3181 30.8037 32.5963 33.083 30.2608 34.615C27.9253 36.1469 25.1208 36.8367 22.325 36.5668C19.5293 36.2969 16.9155 35.0839 14.9291 33.1347C13.8022 32.0298 12.9081 30.7179 12.2979 29.2739C11.6878 27.83 11.3735 26.2822 11.3731 24.719Z" fill="currentColor"/>
                                <path d="M23.5009 14.3056C21.0424 14.3026 18.659 15.1365 16.7569 16.6649C14.8548 18.1934 13.5518 20.3219 13.07 22.6877C12.5882 25.0534 12.9574 27.5098 14.1146 29.6383C15.2719 31.7668 17.1456 33.4356 19.4164 34.3601C21.6871 35.2847 24.2144 35.4079 26.5673 34.7086C28.9202 34.0093 30.9532 32.531 32.3196 30.5254C33.6861 28.5199 34.3015 26.1114 34.0609 23.7105C33.8203 21.3096 32.7386 19.0649 31.0002 17.359C29.0114 15.4063 26.3142 14.3081 23.5009 14.3056ZM23.5009 33.6447C21.3938 33.6468 19.3513 32.9318 17.7214 31.6216C16.0914 30.3113 14.975 28.4869 14.5624 26.4593C14.1498 24.4318 14.4665 22.3265 15.4586 20.5024C16.4507 18.6784 18.0568 17.2484 20.003 16.4563C21.9493 15.6641 24.1152 15.5588 26.1317 16.1584C28.1482 16.7579 29.8904 18.0251 31.0613 19.7441C32.2323 21.463 32.7595 23.5272 32.5531 25.5849C32.3467 27.6426 31.4195 29.5663 29.9295 31.0282C28.2248 32.7022 25.9125 33.6433 23.5009 33.6447Z" fill="currentColor"/>
                                <path d="M44.7245 23.3682V5.81757C44.7999 5.74362 44.8761 5.67646 44.9515 5.6042C46.3609 4.22113 47.6482 2.25661 46.6407 1.26883C45.6332 0.281041 43.6321 1.54255 42.2227 2.92732C40.8133 4.31209 39.5269 6.27491 40.5335 7.2627C40.6529 7.37877 40.7951 7.46981 40.9515 7.53031C41.1078 7.59081 41.2751 7.61949 41.4431 7.61463C42.0778 7.56506 42.6874 7.34961 43.2085 6.99067V23.2271H40.1766V14.7433C40.2519 14.6694 40.3282 14.6022 40.4035 14.53C41.813 13.1469 43.1002 11.1832 42.0928 10.1946C41.0853 9.20597 39.0833 10.4683 37.6748 11.8522C36.2662 13.2362 34.9781 15.199 35.9855 16.1876C36.1049 16.3038 36.2471 16.3949 36.4035 16.4556C36.5598 16.5162 36.7271 16.5451 36.8951 16.5404C37.5306 16.4912 38.141 16.276 38.6632 15.9173V23.3682C38.2212 23.5212 37.8382 23.8046 37.5667 24.1796C37.2953 24.5546 37.1488 25.0028 37.1472 25.4628V28.438C37.147 28.8701 37.2746 29.2929 37.5147 29.6551C37.7547 30.0173 38.0969 30.3033 38.4995 30.4782C38.0351 30.9269 37.6747 31.4686 37.443 32.0664C37.2113 32.6642 37.1137 33.3039 37.1569 33.9421C37.2001 34.5802 37.383 35.2018 37.6931 35.7642C38.0033 36.3267 38.4335 36.8169 38.9543 37.2014L37.3239 39.1192L38.4891 40.0713L40.3325 37.9019C41.2207 38.1756 42.1731 38.1756 43.0613 37.9019L44.9021 40.0713L46.0681 39.1192L44.4369 37.1972C44.9574 36.8125 45.3874 36.3222 45.6974 35.7598C46.0074 35.1973 46.1902 34.5758 46.2334 33.9378C46.2765 33.2997 46.179 32.66 45.9475 32.0623C45.716 31.4646 45.3559 30.9228 44.8917 30.474C45.2941 30.2987 45.6361 30.0127 45.8763 29.6506C46.1165 29.2885 46.2445 28.8658 46.2448 28.4338V25.4585C46.2427 24.9987 46.096 24.5507 45.8247 24.1758C45.5533 23.8009 45.1706 23.5173 44.7288 23.3639L44.7245 23.3682ZM43.2934 3.97546C43.8932 3.34811 44.6105 2.84013 45.4063 2.47933C45.0381 3.26036 44.5201 3.96456 43.8808 4.55351C43.2805 5.18022 42.5632 5.68811 41.7679 6.04964C42.1361 5.26862 42.654 4.56442 43.2934 3.97546ZM38.7455 12.9012C39.3452 12.2739 40.0626 11.7659 40.8583 11.4051C40.4901 12.1861 39.9722 12.8903 39.3328 13.4793C38.7325 14.106 38.0153 14.6139 37.22 14.9754C37.5882 14.1944 38.1061 13.4902 38.7455 12.9012ZM41.6908 36.6183C40.9894 36.6182 40.3096 36.3795 39.7674 35.9428C39.2252 35.5061 38.8541 34.8984 38.7173 34.2233C38.5805 33.5482 38.6865 32.8474 39.0172 32.2403C39.3479 31.6333 39.8829 31.1576 40.5309 30.8942C41.179 30.6308 41.9001 30.5961 42.5713 30.7959C43.2426 30.9958 43.8224 31.4179 44.2121 31.9902C44.6018 32.5626 44.7772 33.2498 44.7084 33.9348C44.6396 34.6198 44.3309 35.2603 43.8348 35.747C43.2669 36.3051 42.4963 36.6192 41.6926 36.62L41.6908 36.6183ZM39.4169 29.1802C39.2158 29.1802 39.023 29.1018 38.8809 28.9623C38.7387 28.8228 38.6589 28.6336 38.6589 28.4363V25.4611C38.6589 25.2638 38.7387 25.0746 38.8809 24.9351C39.023 24.7956 39.2158 24.7173 39.4169 24.7173H43.9648C44.1657 24.7175 44.3584 24.7959 44.5005 24.9354C44.6426 25.0748 44.7225 25.2639 44.7228 25.4611V28.4363C44.7225 28.6335 44.6426 28.8226 44.5005 28.962C44.3584 29.1015 44.1657 29.1799 43.9648 29.1802H39.4169Z" fill="currentColor"/>
                                <path d="M30.0949 11.553C31.1561 12.5944 32.5482 13.5617 33.6024 13.5617C33.7705 13.5664 33.9377 13.5375 34.0941 13.4769C34.2504 13.4163 34.3926 13.3251 34.512 13.209C35.5195 12.2203 34.2322 10.2541 32.8237 8.87359C31.4151 7.49307 29.4123 6.22816 28.4057 7.21594C27.3991 8.20373 28.6872 10.1733 30.0984 11.5556L30.0949 11.553ZM31.7547 9.92428C32.3934 10.5138 32.9107 11.2182 33.2784 11.9993C32.4822 11.6382 31.7642 11.13 31.1639 10.5023C30.525 9.91361 30.0077 9.20969 29.6401 8.429C30.4356 8.79058 31.1529 9.29879 31.7529 9.92598L31.7547 9.92428Z" fill="currentColor"/>
                                <path d="M13.4001 13.5617C14.4544 13.5617 15.8491 12.591 16.9077 11.5539C18.3171 10.1699 19.6044 8.20628 18.5969 7.21849C17.5894 6.23071 15.5875 7.49137 14.1789 8.87529C12.7703 10.2592 11.4822 12.2229 12.4897 13.2107C12.6095 13.3271 12.7523 13.4183 12.9093 13.4787C13.0663 13.5391 13.2342 13.5673 13.4027 13.5617H13.4001ZM15.2479 9.92343C15.8478 9.29696 16.5652 8.78985 17.3607 8.42985C16.9925 9.21087 16.4746 9.91507 15.8352 10.504C15.2351 11.1309 14.5178 11.6388 13.7224 12.0002C14.0906 11.2191 14.6085 10.5149 15.2479 9.92598V9.92343Z" fill="currentColor"/>
                                <path d="M8.3411 23.3682V15.9173C8.86166 16.2749 9.47044 16.4889 10.104 16.537C10.2721 16.5421 10.4394 16.5134 10.5959 16.4527C10.7523 16.3921 10.8944 16.3007 11.0136 16.1842C12.021 15.1964 10.7337 13.2302 9.32432 11.8488C7.9149 10.4675 5.91554 9.20682 4.90634 10.1955C3.89713 11.1841 5.18615 13.1495 6.59644 14.5308C6.67267 14.6048 6.74803 14.6728 6.82427 14.7442V23.233H3.79231V6.99662C4.31286 7.35422 4.92164 7.56822 5.55517 7.61633C5.72328 7.62138 5.89066 7.5927 6.04706 7.53204C6.20347 7.47137 6.3456 7.38001 6.46476 7.26355C7.47483 6.27746 6.18842 4.31209 4.77726 2.92817C3.36611 1.54425 1.36675 0.28189 0.35928 1.26883C-0.648194 2.25576 0.639086 4.22368 2.04938 5.6042C2.12561 5.67816 2.20097 5.74617 2.2772 5.81757V23.3716C1.83564 23.5245 1.45301 23.8076 1.18165 24.1821C0.910293 24.5565 0.763456 25.0041 0.76123 25.4636V28.4389C0.760631 28.871 0.888158 29.294 1.12826 29.6562C1.36837 30.0185 1.71068 30.3044 2.11348 30.4791C1.64906 30.9278 1.28871 31.4695 1.05702 32.0672C0.825325 32.665 0.727735 33.3048 0.770903 33.9429C0.81407 34.5811 0.996981 35.2026 1.30716 35.7651C1.61734 36.3276 2.04749 36.8178 2.56827 37.2023L0.940549 39.1192L2.10568 40.0713L3.94911 37.9019C4.83731 38.1756 5.78966 38.1756 6.67787 37.9019L8.51869 40.0713L9.68469 39.1192L8.0535 37.1972C8.57404 36.8125 9.00396 36.3222 9.31397 35.7598C9.62398 35.1973 9.80679 34.5758 9.84995 33.9378C9.89312 33.2997 9.79561 32.66 9.56409 32.0623C9.33256 31.4645 8.97245 30.9228 8.50829 30.474C8.91072 30.2987 9.25273 30.0127 9.49289 29.6506C9.73305 29.2885 9.86106 28.8658 9.86141 28.4338V25.4585C9.85934 24.9987 9.71261 24.5507 9.44126 24.1758C9.16992 23.8009 8.7872 23.5173 8.34544 23.3639L8.3411 23.3682ZM6.14251 11.4051C6.93854 11.7662 7.65619 12.2745 8.25621 12.9021C8.89504 13.491 9.41263 14.1949 9.78085 14.9754C8.98482 14.6143 8.26717 14.106 7.66715 13.4784C7.02804 12.8898 6.51041 12.1858 6.14251 11.4051ZM1.59458 2.47933C2.39216 2.83997 3.11132 3.34826 3.71262 3.97631C4.35144 4.56523 4.86904 5.2691 5.23725 6.04964C4.44123 5.68853 3.72357 5.18027 3.12355 4.55266C2.48289 3.96436 1.96377 3.26041 1.59458 2.47933ZM5.30915 36.62C4.6077 36.6199 3.92796 36.3812 3.38576 35.9445C2.84356 35.5078 2.47244 34.9001 2.33564 34.225C2.19884 33.5499 2.30482 32.8491 2.63552 32.242C2.96623 31.635 3.50119 31.1593 4.14927 30.8959C4.79735 30.6325 5.51843 30.5978 6.18967 30.7976C6.86091 30.9975 7.44077 31.4196 7.83044 31.9919C8.22012 32.5643 8.3955 33.2515 8.32671 33.9365C8.25792 34.6215 7.9492 35.262 7.45318 35.7487C7.17161 36.0249 6.83735 36.2441 6.46948 36.3936C6.10161 36.5431 5.70733 36.62 5.30915 36.62ZM3.03519 29.1819C2.83416 29.1819 2.64136 29.1035 2.49921 28.964C2.35706 28.8245 2.2772 28.6353 2.2772 28.438V25.4628C2.2772 25.2655 2.35706 25.0763 2.49921 24.9368C2.64136 24.7973 2.83416 24.719 3.03519 24.719H7.58312C7.78408 24.7192 7.97674 24.7976 8.11884 24.9371C8.26094 25.0765 8.34088 25.2656 8.3411 25.4628V28.438C8.34088 28.6352 8.26094 28.8243 8.11884 28.9637C7.97674 29.1032 7.78408 29.1816 7.58312 29.1819H3.03519Z" fill="currentColor"/>
                            </svg>
                        </span>
                    </button>

                    <button
                        class="btn collapse-square mh-1 tw-rounded-full"
                        :title="loop ? 'Disable Loop' : 'Enable Loop'"
                        @click.stop="toggleLoop"
                        @keydown.prevent
                    >
                        <span
                            class="bg-white tw-rounded-full"
                            :class="loop ? 'text-grey-5' : 'inverted text-white'"
                        >
                            <i class="fa fa-repeat"></i>
                        </span>
                    </button>
                </div>
            </div>

            <div class="flex flex-column grow ph-1">
                <div class="flex flex-row">
                    <div class="flex flex-column grow"></div>

                    <div
                        v-if="!isMobile"
                        class="flex flex-column volume"
                    >
                        <div class="flex flex-row align-h-right align-v-center">
                            <div class="flex flex-column volume-rail-wrap">
                                <div class="volume-rail">
                                    <div
                                        class="volume-fill bg-white"
                                        :style="volumeOffset"
                                        @click.stop.stop
                                    ></div>

                                    <input
                                        type="range"
                                        class="volume-range"
                                        min="0"
                                        max="100"
                                        :value="currentVolume"
                                        @click.stop.stop
                                        @input="emitVolumeChange"
                                    >
                                </div>
                            </div>

                            <button
                                :title="currentVolume === 0 ? 'Unmute (M)' : 'Mute (M)'"
                                class="btn collapse-square text-white"
                                @click.stop="emitVolumeChange(0)"
                                @keydown.prevent
                            >
                                <span
                                    class="text-white flat"
                                >
                                    <i
                                        class="fa"
                                        :class="volumeButtonClass"
                                    ></i>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import PlayerUtils from '../../Components/VideoPlayer/player-utils';

export default {
    name: 'PlayAlongsPlayer',
    props: {
        activeItem: {
            type: Object,
            default: () => null,
        },

        audioPlayer: {
            type: [Object, HTMLAudioElement],
            default: () => ({}),
        },

        isPlaying: {
            type: Boolean,
            default: () => false,
        },

        noSidebar: {
            type: Boolean,
            default: () => false,
        },

        metronome: {
            type: Boolean,
            default: () => false,
        },

        drums: {
            type: Boolean,
            default: () => false,
        },

        loop: {
            type: Boolean,
            default: () => false,
        },

        anchorOffsets: {
            type: Object,
            default: () => ({
                a: 0,
                b: 100,
            }),
        },

        currentTime: {
            type: Number,
            default: () => 0,
        },

        totalDuration: {
            type: Number,
            default: () => 0,
        },

        currentPosition: {
            type: Number,
            default: () => 0,
        },

        currentMouseX: {
            type: Number,
            default: () => 0,
        },

        playedContent: {
            type: Array,
            default: () => [],
        },

        currentVolume: {
            type: Number,
            default: () => 100,
        },

        totalResults: {
            type: Number,
            default: () => 0,
        },

        isShuffle: {
            type: Boolean,
            default: () => false,
        },
    },
    data() {
        return {
            mousedown: false,
            collapsed: false,
            volumeCache: this.currentVolume,
        };
    },
    computed: {
        isMobile: () => PlayerUtils.isMobile().any,

        volumeOffset() {
            return {
                width: `${this.currentVolume}%`,
            };
        },

        volumeButtonClass() {
            return {
                'fa-volume-slash': this.currentVolume === 0,
                'fa-volume-down': this.currentVolume > 0 && this.currentVolume < 50,
                'fa-volume-up': this.currentVolume >= 50,
            };
        },

        $_title() {
            return this.activeItem ? this.activeItem.post.title : '';
        },

        $_style() {
            return this.activeItem && this.activeItem.post.style ? this.activeItem.post.style.join(', ') : '';
        },

        $_bpm() {
            return this.activeItem ? this.activeItem.post.bpm : '';
        },

        durationOffsetStyles() {
            if (this.mousedown) {
                const percentOffset = (this.currentMouseX / this.$refs.progressBar.clientWidth) * 100;

                return {
                    transform: `translateX(${percentOffset - 100}%)`,
                    'webkit-transform': `translateX(${percentOffset - 100}%)`,
                };
            }

            return {
                transform: `translateX(${this.currentPosition - 100}%)`,
                'webkit-transform': `translateX(${this.currentPosition - 100}%)`,
            };
        },

        $_isPlaying: {
            cache: false,
            get() {
                return this.audioPlayer ? !this.audioPlayer.paused : false;
            },
        },

        anchorOffsetsInSeconds() {
            return {
                a: (this.anchorOffsets.a / 100) * this.totalDuration,
                b: (this.anchorOffsets.b / 100) * this.totalDuration,
            };
        },

        disablePreviousTrack() {
            if (this.isShuffle) {
                return this.playedContent.length < 2;
            }

            return false;
        },
    },
    watch: {
        collapsed() {
            if (this.collapsed) {
                document.body.classList.remove('play-alongs-player-open');
            } else {
                document.body.classList.add('play-alongs-player-open');
            }
        },

        activeItem() {
            if (this.activeItem != null && !this.collapsed) {
                document.body.classList.add('play-alongs-player-open');
            } else {
                document.body.classList.remove('play-alongs-player-open');
            }
        },
    },
    mounted() {
        document.addEventListener('mouseup', this.handleMouseUp);
    },
    beforeDestroy() {
        document.removeEventListener('mouseup', this.handleMouseUp);
    },
    methods: {
        playPause() {
            this.$emit('playPause');
            this.$nextTick(() => this.$forceUpdate());
        },

        seek(position) {
            this.$emit('seek', position);
        },

        toggleDrums() {
            this.$emit('drums', !this.drums);
        },

        toggleMetronome() {
            this.$emit('metronome', !this.metronome);
        },

        toggleLoop() {
            this.$emit('loop', !this.loop);
        },

        skipFive(forward = true) {
            const newPosition = forward ? this.currentTime + 5 : this.currentTime - 5;
            this.seek(newPosition);
        },

        nextTrack() {
            this.$emit('nextTrack');
        },

        previousTrack() {
            this.$emit('previousTrack');
        },

        seekViaProgressBar(event) {
            const percentToSeekTo = (event.clientX || event.touches[0].clientX) / this.$refs.progressBar.clientWidth;
            const offsetToSeekTo = this.totalDuration * percentToSeekTo;

            this.$emit('seek', offsetToSeekTo);
        },

        getAnchorOffsetTime(offset) {
            return this.parseTime(this.totalDuration * (offset / 100));
        },

        handleMouseUp() {
            if (this.mousedown) {
                const percentToSeekTo = (this.currentMouseX / this.$refs.progressBar.clientWidth);
                const offsetToSeekTo = this.totalDuration * percentToSeekTo;

                this.$emit('seek', offsetToSeekTo);
            }

            setTimeout(() => {
                this.mousedown = false;
            }, 100);
        },

        emitAnchorMouseDown(anchor) {
            this.$emit('anchorMouseDown', anchor);
        },

        emitAnchorButtonClick(anchor) {
            this.$emit('anchorButtonClick', anchor);
        },

        emitVolumeChange(event) {
            if (event) {
                this.$emit('volumeChange', event.target.value);
            } else {
                let volume = this.volumeCache;

                if (this.currentVolume) {
                    volume = 0;
                    this.volumeCache = this.currentVolume;
                }

                this.$emit('volumeChange', volume);
            }
        },

        parseTime: time => PlayerUtils.parseTime(time),
    },
};
</script>
